import cn from 'classnames';
import { useCallback, useMemo } from 'react';
import { IndexedStep } from 'components/Wizard';
import { createChainedFunction } from 'utils/functions';
import { WizardFlow } from './types';

export default function useMergeFlows<TStepId extends string = string>(
  flow1: WizardFlow<TStepId>,
  flow2: WizardFlow<TStepId>,
  /**
   * The subflow step swill be inserted after the root step
   */
  rootStepId?: TStepId,
): WizardFlow<TStepId> {
  const handleStepClick = useCallback(
    async (toStep: IndexedStep<TStepId>, fromStep: IndexedStep<TStepId>) => {
      // Absent handlers default to `true` (allow the click). Without this,
      // `undefined` short-circuits the `!flow1CanLeave` check and blocks every
      // step-nav click whenever either flow doesn't supply an `onStepClick`.
      const flow1CanLeave =
        (await flow1.onStepClick?.(toStep, fromStep)) ?? true;

      if (!flow1CanLeave) {
        return false;
      }

      const flow2CanLeave =
        (await flow2.onStepClick?.(toStep, fromStep)) ?? true;

      return flow1CanLeave && flow2CanLeave;
    },
    [flow1, flow2],
  );

  const mergedSteps = useMemo(() => {
    const steps = [...flow1.steps];
    const rootStepIndex = rootStepId
      ? steps.findIndex(step => step.stepId === rootStepId)
      : steps.length - 1;
    steps.splice(rootStepIndex + 1, 0, ...flow2.steps);

    return steps;
  }, [flow1.steps, flow2.steps, rootStepId]);

  return {
    bodyClassName: cn(flow1.bodyClassName, flow2.bodyClassName),
    buttonClassName: cn(flow1.buttonClassName, flow2.buttonClassName),
    canBacktrack: (flow1.canBacktrack ?? true) && (flow2.canBacktrack ?? true),
    onStepChange: createChainedFunction(flow1.onStepChange, flow2.onStepChange),
    onStepClick: handleStepClick,
    steps: mergedSteps,
  };
}
